import resultsUnitTypes from "@wesstron/utils/results/resultsUnitTypes";
import ResultSummaryTypes from "@wesstron/utils/results/resultsSummaryTypes";
import {convertWeightUnitTo, getUnit} from "./UnitUtils";
import {createKeyToValueDictionary} from "./Utils";
import i18n from "../i18n";
import {UnitTypes} from "../constans/unitTypes";

export const RESULT_TARGET_REGEX = new RegExp("{{\\w*}}", "gm");

function createTargetRangeForFormula(sqlKey, targets, targetDictionary) {
    const array = [];
    for (let i = 0; i < 2; i++) {
        // petla po ilosci elementow, aktualnie min - max
        let tmp = targetDictionary[sqlKey].formula;
        const res = tmp.match(RESULT_TARGET_REGEX);
        for (let item of res) {
            const name = item.slice(2, -2);
            const target = targets?.[name]
                ? targets[name].target[i]
                : targetDictionary[name]?.default[i];
            tmp = tmp.replace(item, target);
        }
        tmp = tmp.replace(/[^-()\d/*+.]/g, "");
        // eslint-disable-next-line no-new-func
        const value = new Function(`return ${tmp}`)();
        switch (targetDictionary[sqlKey].round) {
            case "round":
                array.push(Math.round(value));
                break;
            case "floor":
                array.push(Math.floor(value));
                break;
            case "ceil":
                array.push(Math.ceil(value));
                break;
            default:
                array.push(value);
        }
    }
    return array;
}

export function getTargetRange(sqlKey, targets, targetDictionary) {
    if (targetDictionary[sqlKey].component) return null;
    if (targetDictionary[sqlKey].formula)
        return createTargetRangeForFormula(sqlKey, targets, targetDictionary);
    if (targets?.[sqlKey]?.target) return targets[sqlKey].target;
    if (targetDictionary[sqlKey].hasOwnProperty("default"))
        return targetDictionary[sqlKey].default;
    return [];
}

function getValueTarget(customTarget, rowValue) {
    if (!rowValue) return 0;
    let tmp = customTarget;
    const res = tmp.match(RESULT_TARGET_REGEX);
    for (let item of res) {
        const name = item.slice(2, -2);
        const target = rowValue[name] || 0;
        tmp = tmp.replace(item, target);
    }
    tmp = tmp.replace(/[^-()\d/*+.]/g, "");
    // eslint-disable-next-line no-new-func
    return new Function(`return ${tmp}`)();
}

export function isInTargetRange(rowValue, valueKey, target, customTarget) {
    if (typeof target === "function") return target(rowValue);
    const value =
        (customTarget
            ? getValueTarget(customTarget, rowValue)
            : rowValue?.[valueKey]) || 0;
    return value >= target[0] && value <= target[1];
}

export function formatResultValue(value, format, animalUnit) {
    switch (format) {
        case "float":
            return +value.toFixed(2);
        case "weight":
            const unit = convertResultWeightUnit(animalUnit);
            return convertWeightUnitTo(value, {
                unit: unit,
                showUnit: false,
                fixed: 2,
                rawValue: true,
            });
        default:
            return value;
    }
}

export function getQueryDictionary(resultsType, res) {
    if (resultsType === "weeks") return createKeyToValueDictionary(res, "week");
    if (resultsType === "months")
        return createKeyToValueDictionary(res, "month");
    if (resultsType === "year") return createKeyToValueDictionary(res, "year");
    if (resultsType === "weekDay")
        return createKeyToValueDictionary(res, "day");
    return createKeyToValueDictionary(res, "group_nr");
}

// The aggregate the desktop summary column shows for a metric: the "summary"
// fetch (getResults(..., "summary")) returns one row per metric; read the
// metric's own valueKey off it, falling back to `val`. Metrics with
// summaryType NONE - or a failed/empty summary - have no meaningful
// aggregate, so return null. When the API does return a row but its value is
// null/undefined, show 0 (the aggregate exists, it's just empty). Shared by
// the desktop summary column, the mobile summary chip, and the chart's
// summary card so all three show one value.
export function getResultSummaryValue(item, summaryRow) {
    if (
        !item ||
        item.summaryType === ResultSummaryTypes.NONE ||
        !summaryRow ||
        summaryRow.status === "ERROR" ||
        !summaryRow.data?.[0]
    ) {
        return null;
    }
    const row = summaryRow.data[0];
    const value = row.hasOwnProperty(item.valueKey)
        ? row[item.valueKey]
        : row.val;
    return value ?? 0;
}

// i18n key naming what the summary aggregate actually is. SUM/AVG map to their
// own words; anything else (CUSTOM, or a mix that can't be named
// unambiguously) falls back to the generic "summary".
export function getResultSummaryLabelKey(summaryType) {
    switch (summaryType) {
        case ResultSummaryTypes.SUM:
            return "grid.sum";
        case ResultSummaryTypes.AVG:
            return "avg";
        default:
            return "summary";
    }
}

export function createRowGroups(rows) {
    const groups = [];
    let currentGroup = null;
    for (let row of rows) {
        if (row.group) {
            if (currentGroup) groups.push(currentGroup);
            currentGroup = {
                group: row.group,
                items: [row],
            };
        } else {
            currentGroup.items.push(row);
        }
    }
    if (currentGroup) groups.push(currentGroup);
    return groups;
}

const AIKeyMap = {
    inseminatedGilts: "inseminatedGiltsAmount",
    inseminatedSows: "inseminatedSowsAmount",
    inseminatedTotal: "inseminatedAmount",
    repetitionGilts: "repetitionGiltsAmount",
    repetitionSows: "repetitionSowsAmount",
    repetitionTotal: "repetitionAmount",
    ultrasoundExaminationAmount: "usgAmount",
    farrowedSows: "farrowedAmount",
    totalBornPiglets: "bornPiglets",
    avgAllBornPigletsPerSow: "averageBornPigletsForSow",
    totalBornAlivePiglets: "aliveBornPiglets",
    avgBornAlivesPigletsPerSow: "aliveBornPigletsForSow",
    totalWeakBornPiglet: "weakBornPiglets",
    avgWeakBornPigletPerSow: "weakBornPigletsForSow",
    totalMummiesBornPiglet: "mummyBornPiglets",
    avgMummiesBornPigletPerSow: "mummyBornPigletsForSow",
    totalDeadBornPiglet: "deadBornPiglets",
    avgDeadPigletPerSow: "deadBornPigletsForSow",
    avgWeightBornAlivePiglet: "averagePigletWeight",
    weanedSows: "separatedLitters",
    totalWeanedPiglet: "separatedPiglets",
    avgWeanedPigletPerSow: "separatedPigletForSow",
    avgWeightWeanedPiglet: "averageSeparatedPigletWeight",
    totalFallPiglets: "deadPiglets",
    inseminationRate: "inseminationEffectiveness",
    avgDaysFromInseminationToRepetition: "averageRepetitionTime",
    avgAmountDaysBetweenWeanedAndInsemination:
        "averageSeparationToInseminationDays",
    farrowRate: "farrowingEffectiveness",
    farrowRateGilts: "giltsFarrowingEffectiveness",
    farrowRateSows: "sowsFarrowingEffectiveness",
};

export function remapAIKeyToRowKey(key) {
    return AIKeyMap[key] || key;
}

export function getResultUnit(unit) {
    if (!unit) return "";
    switch (unit) {
        case resultsUnitTypes.PERCENT:
            return "%";
        case resultsUnitTypes.WEIGHT:
            return getUnit("weight", UnitTypes.MEDIUM);
        case resultsUnitTypes.BIG_WEIGHT:
            return getUnit("weight", UnitTypes.BIG);
        case resultsUnitTypes.SMALL_WEIGHT:
            return getUnit("weight", UnitTypes.SMALL);
        default:
            return i18n.t(unit);
    }
}

export function convertResultWeightUnit(unit) {
    switch (unit) {
        case resultsUnitTypes.WEIGHT:
            return UnitTypes.MEDIUM;
        case resultsUnitTypes.BIG_WEIGHT:
            return UnitTypes.BIG;
        case resultsUnitTypes.SMALL_WEIGHT:
            return UnitTypes.SMALL;
        default:
            return null;
    }
}
